// language.ts - the shared YAML+SQL CodeMirror 6 language. The Lezer YAML
// grammar supplies document structure; `parseMixed` nests two stream
// tokenizers into its scalar leaves:
//
//   BlockLiteralContent under a SQL key, or a direct child of a top-level
//   SQL parent (the `queries.<name>: |` shorthand) -> SQL (+ Jinja)
//   every other scalar body                        -> the YAML scalar tokenizer
//
// The second nesting is what makes scalars color by *type* rather than by
// quoting: `@lezer/yaml` tags every plain scalar `tags.content` whatever it
// holds, so `title: Foo` would render unstyled next to a red `label: "Foo"`.
// See dbt-charts/src/dbt_charts/data/highlighting/AGENTS.md.
//
// The SQL block rules are a required, validated parameter: this package makes
// no assumption about which keys or parents hold SQL (that manifest lives in
// dbt-charts/src/dbt_charts/data/highlighting/board.json and is the caller's
// responsibility to pass through).

import { type Language, type StreamParser, StreamLanguage } from "@codemirror/language";
import { yamlLanguage } from "@codemirror/lang-yaml";
import type { StringStream } from "@codemirror/language";
import { parseMixed, type Input, type SyntaxNode, type SyntaxNodeRef } from "@lezer/common";
import { sql as sqlMode } from "@codemirror/legacy-modes/mode/sql";
import type { Extension } from "@codemirror/state";

// `toggleComment` (bound to Mod-/ in dbtChartsKeymap) reads `commentTokens`
// language data and uses `data[0]`. DuckDB/Postgres-style SQL has no `#` line
// comment, so both the YAML and SQL sides declare their own real tokens. A
// `query:` body is nested SQL in the tree, so CM resolves `--` there by itself.
const SQL_COMMENT_TOKENS = { line: "--", block: { open: "/*", close: "*/" } };

const wordSet = (words: string): Record<string, boolean> =>
  Object.fromEntries(words.trim().split(/\s+/).map((w) => [w, true]));

// dbt charts targets DuckDB/Postgres-style analytical SQL. The stock CM
// dialects (standardSQL, pgSQL) miss common analytical keywords - WITH,
// INTERVAL, QUALIFY, window clauses, set ops - so highlight against this
// curated set. Both the dashboard's embedded SQL and the standalone chart SQL
// editor use it, so keyword coverage is uniform across surfaces.
const DBT_CHARTS_SQL_KEYWORDS = wordSet(`
    with recursive select distinct from where group by having qualify
    window over partition order asc desc nulls first last limit offset
    fetch row rows only union all except intersect
    join inner left right full outer cross natural on using lateral
    as case when then else end cast try_cast interval
    and or not in is between like ilike similar to escape
    any all some exists
    insert into values update set delete create replace temporary temp table
    view if exists pivot unpivot unnest filter within ordering
    rollup cube grouping sets distinct_on returning
`);
const DBT_CHARTS_SQL_BUILTINS = wordSet(`
    count sum avg min max median stddev variance
    row_number rank dense_rank ntile lag lead first_value last_value
    coalesce nullif greatest least
    date_trunc date_diff date_part extract now today current_date
    upper lower trim length substring regexp_replace concat
`);

const dbtChartsSqlMode: StreamParser<unknown> = {
  ...sqlMode({
    keywords: DBT_CHARTS_SQL_KEYWORDS,
    builtin: DBT_CHARTS_SQL_BUILTINS,
    atoms: wordSet("true false null unknown"),
    dateSQL: wordSet("date time timestamp"),
  }),
  languageData: { commentTokens: SQL_COMMENT_TOKENS },
};

/** Standalone SQL language for the chart editor. */
export const sqlLanguage: Language = StreamLanguage.define(dbtChartsSqlMode);

// Tokenize a Jinja delimiter run ({{ }}, {% %}, {# #}) a board uses for
// templating, so it reads as templating rather than as the text around it.
// Line-scoped by design: a run left open at end of line ends there rather than
// bleeding template color across the rest of the document.
function tokenJinja(stream: StringStream): string | null {
  const delimiters: [string, string, string][] = [
    ["{{", "}}", "meta"],
    ["{%", "%}", "keyword"],
    ["{#", "#}", "comment"],
  ];
  for (const [open, close, style] of delimiters) {
    if (!stream.match(open)) continue;
    while (!stream.eol()) {
      if (stream.match(close)) break;
      stream.next();
    }
    return style;
  }
  return null;
}

const sqlBlockParser = StreamLanguage.define<unknown>({
  ...dbtChartsSqlMode,
  token: (stream, state) => tokenJinja(stream) ?? dbtChartsSqlMode.token(stream, state),
}).parser;

// `@lezer/yaml` has no number/boolean/null nodes - `0`, `no` and `Existing
// Customers` are all `Literal` - so the type has to be recognized here. These
// mirror apps/vscode-extension/syntaxes/dbt-charts.tmLanguage.json exactly:
// the two editors must agree on what a scalar is.
const YAML_NUMBER_RE = /^[-+]?\d+(\.\d+)?([eE][+-]?\d+)?$/;
const YAML_BOOL_RE = /^(true|True|TRUE|false|False|FALSE|yes|Yes|YES|no|No|NO)$/;
const YAML_NULL_RE = /^(null|Null|NULL|~)$/;

function scalarTokenType(text: string): string {
  if (YAML_NUMBER_RE.test(text)) return "number";
  if (YAML_BOOL_RE.test(text)) return "bool";
  if (YAML_NULL_RE.test(text)) return "null";
  return "string";
}

// A scalar holding a Jinja delimiter is a string by construction - `title:
// 100{{ n }}` is not a number - so it routes to the string tokenizer rather
// than being typed piecewise.
const JINJA_OPEN = /\{[{%#]/;

/** Literal scalar text up to the next `{`, so Jinja delimiters stay reachable. */
function tokenScalarText(stream: StringStream): string {
  stream.next(); // never zero-width: tokenJinja already declined at this position
  if (!stream.skipTo("{")) stream.skipToEnd();
  return "string";
}

/**
 * Tokenizes one scalar body, which `parseMixed` hands over a line at a time.
 * `typed` is false for anything that is a string by definition however its
 * text reads: `|`/`>` bodies, multi-line plain scalars, and anything holding
 * Jinja - which the string branch interleaves so `{{ }}` reads as templating
 * inside a `title:` exactly as it does inside a `query:`.
 *
 * `commentTokens` is restated because a nested language *replaces* the outer
 * one's language data rather than extending it - without this, `Mod-/` is a
 * dead key inside a scalar body. A `#` there is YAML-scalar text on a YAML
 * line, so the outer answer is also the right one.
 */
function yamlScalarMode(typed: boolean): StreamParser<unknown> {
  return {
    token(stream) {
      stream.eatSpace();
      if (stream.eol()) return null;
      if (typed) {
        const text = stream.string.slice(stream.pos).trimEnd();
        stream.skipToEnd();
        return scalarTokenType(text);
      }
      return tokenJinja(stream) ?? tokenScalarText(stream);
    },
    languageData: { commentTokens: { line: "#" } },
  };
}

const typedScalarParser = StreamLanguage.define(yamlScalarMode(true)).parser;
const stringScalarParser = StreamLanguage.define(yamlScalarMode(false)).parser;

/** Which block scalars hold SQL - the `sql_block_scalar_*` lists of the highlight manifest. */
export interface SqlBlockRules {
  /** Keys whose `|`/`>` bodies hold SQL wherever they appear (`query`, `sql`). */
  keys: string[];
  /** Top-level keys whose direct-child block scalars hold SQL (`queries`: the shorthand). */
  parents: string[];
}

function validateNames(what: string, names: string[]): void {
  if (!Array.isArray(names)) {
    throw new Error(`dbtChartsYamlLanguage requires an array of ${what}`);
  }
  for (const name of names) {
    if (typeof name !== "string" || name.length === 0) {
      throw new Error(`dbtChartsYamlLanguage requires non-empty string ${what}, got: ${JSON.stringify(name)}`);
    }
  }
}

function validateRules(rules: SqlBlockRules): void {
  validateNames("block-scalar keys", rules.keys);
  if (rules.keys.length === 0) {
    throw new Error("dbtChartsYamlLanguage requires a non-empty array of block-scalar keys");
  }
  validateNames("block-scalar parents", rules.parents);
}

function pairKey(pair: SyntaxNode | null | undefined, input: Input): string | null {
  const key = pair?.getChild("Key");
  return key ? input.read(key.from, key.to) : null;
}

/** The key of the pair whose mapping holds `pair`, when that mapping sits directly under the document. */
function topLevelParentKey(pair: SyntaxNode, input: Input): string | null {
  const parentPair = pair.parent?.parent;
  if (parentPair?.name !== "Pair" || parentPair.parent?.parent?.name !== "Document") return null;
  return pairKey(parentPair, input);
}

/** Whether a `BlockLiteralContent` sits under a SQL key, or is a direct child of a top-level SQL parent. */
function isSqlBlock(node: SyntaxNodeRef, input: Input, keys: Set<string>, parents: Set<string>): boolean {
  const pair = node.node.parent?.parent;
  if (pair?.name !== "Pair") return false;
  const key = pairKey(pair, input);
  if (key !== null && keys.has(key)) return true;
  const parent = topLevelParentKey(pair, input);
  return parent !== null && parents.has(parent);
}

/** The shared dbt charts YAML+SQL language. `rules` names which `|`/`>` bodies hold SQL. */
export function dbtChartsYamlLanguage(rules: SqlBlockRules): Extension {
  validateRules(rules);
  const sqlKeys = new Set(rules.keys);
  const sqlParents = new Set(rules.parents);
  const wrap = parseMixed((node, input) => {
    if (node.name === "BlockLiteralContent") {
      return { parser: isSqlBlock(node, input, sqlKeys, sqlParents) ? sqlBlockParser : stringScalarParser };
    }
    // A `Key`'s own scalar is already tagged propertyName - structure outranks
    // content, so a key named `no` is not a boolean.
    if (node.node.parent?.name === "Key") return null;
    // `@lezer/yaml` tags a QuotedLiteral `string` wholesale, so the quoted form
    // of a template needs its own nesting site. The quotes tokenize as string
    // either way, so the scalar still reads as a string with Jinja inside it.
    if (node.name === "QuotedLiteral") return { parser: stringScalarParser };
    if (node.name === "Literal") {
      const text = input.read(node.from, node.to);
      const typed = !text.includes("\n") && !JINJA_OPEN.test(text);
      return { parser: typed ? typedScalarParser : stringScalarParser };
    }
    return null;
  });
  return yamlLanguage.configure({ wrap });
}
